import _ from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resourceAdd } from '../../actions/resources.actions';
import {
  ADMIN_CONTACT_CITY,
  ADMIN_CONTACT_CITY_ROLES_OPTIONS,
  ADMIN_CONTACT_PARTNER,
  ADMIN_CONTACT_PARTNER_ROLES_OPTIONS,
  ADMIN_CONTACT_SCHOOL,
  ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS,
  ADMIN_CONTACT_TYPES
} from '../../constants/admin.contacts.constants';
import { ALL_SELECT_OPTION } from '../../constants/settings.constants';
import { ADMIN_CONTACTS_RESOURCE } from '../../resources/admin/admin.contacts.resource';
import { ADMIN_CONTACTS_ADVANCED_SEARCH_RESOURCE } from '../../resources/advanced-search/admin.contacts.advanced.search.resource';
import { advancedSearchFiltersSelector } from '../../selectors/advanced.search.selectors';
import { currentUserSelector } from '../../selectors/auth.selectors';
import adminSchoolsService from '../../services/api/admin.schools.service';
import partnersService from '../../services/api/partners.service';
import permissionsService from '../../services/permissions/permissions.service';
import { useAdvancedSearchBase } from '../useAdvancedSearchBase.hook';
import { useDependency } from '../useDependency.hook';

const SEARCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'function',
  'role',
  'type',
  'net',
  'management',
  'community',
  'school',
  'branch',
  'organization'
];
const DEFAULT_FILTERS_STATE = {
  type: ALL_SELECT_OPTION.value,
  net: [ALL_SELECT_OPTION.value],
  management: [ALL_SELECT_OPTION.value],
  community: [ALL_SELECT_OPTION.value],
  school: [ALL_SELECT_OPTION.value],
  branch: [ALL_SELECT_OPTION.value],
  organization: [ALL_SELECT_OPTION.value],
  roles: [ALL_SELECT_OPTION.value]
};
const dataResource = ADMIN_CONTACTS_RESOURCE;
const filtersResource = ADMIN_CONTACTS_ADVANCED_SEARCH_RESOURCE;

export const useContactsAdvancedSearch = () => {
  const dispatch = useDispatch();
  const advancedSearch = useAdvancedSearchBase(filtersResource, dataResource, {
    searchFields: SEARCH_FIELDS,
    dateFields: [],
    defaultFiltersState: DEFAULT_FILTERS_STATE
  });
  const user = useSelector(currentUserSelector);
  const filters = useSelector((state) => advancedSearchFiltersSelector(state, { resource: filtersResource }));
  const schoolNets = useDependency(adminSchoolsService.getSchoolNetsOptions);
  const organizations = useDependency(partnersService.getPartnersOptions);
  const schoolManagements = useDependency(adminSchoolsService.getAllManagements, {
    mapResponse: (items) => items?.map((i) => ({ ...i, value: i.id, label: i.name }))
  });
  const schoolCommunities = useDependency(adminSchoolsService.getAllCommunities, {
    mapResponse: (items) => items?.map((i) => ({ ...i, value: i.id, label: i.name }))
  });
  const schools = useDependency(adminSchoolsService.getAllSchools, {
    mapResponse: (items) => items?.map((i) => ({ ...i, value: i.id, label: i.name }))
  });
  const branches = {
    isLoading: schools.isLoading,
    loadError: schools.loadError,
    options: _.flatten(
      schools.options?.map(
        (s) =>
          s?.branches?.map((b) => ({ ...b, value: b.id, label: b.name, schoolId: s.id, communityId: s.communityId })) ??
          []
      )
    )
  };
  const dependencies = {
    contactTypes: { isLoading: false, options: ADMIN_CONTACT_TYPES },
    roles: {
      isLoading: false,
      options:
        permissionsService.isSchoolEmployee(user) &&
        !permissionsService.isPartnerEmployee(user) &&
        !permissionsService.isCityEmployee(user)
          ? ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS
          : filters?.type === ADMIN_CONTACT_CITY
          ? ADMIN_CONTACT_CITY_ROLES_OPTIONS
          : filters?.type === ADMIN_CONTACT_PARTNER
          ? ADMIN_CONTACT_PARTNER_ROLES_OPTIONS
          : filters?.type === ADMIN_CONTACT_SCHOOL
          ? ADMIN_CONTACT_SCHOOL_ROLES_OPTIONS
          : []
    },
    schoolNets: schoolNets,
    schoolManagements: schoolManagements,
    schoolCommunities: schoolCommunities,
    schools: schools,
    branches: branches,
    organizations
  };

  const fetchDependencies = (type) => {
    if (type === ADMIN_CONTACT_PARTNER) {
      organizations.onLoad();
    }
    if (type === ADMIN_CONTACT_SCHOOL) {
      schoolNets.onLoad();
      schoolCommunities.onLoad();
      schoolManagements.onLoad();
      schools.onLoad();
    }
  };

  const onInput = (value, field) => {
    if (field === 'type') {
      fetchDependencies(value);
    }
    advancedSearch?.onInput(value, field);
  };

  useEffect(() => {
    dispatch(resourceAdd({ key: filtersResource.key }, { filters: { ...DEFAULT_FILTERS_STATE } }));
  }, [dispatch]);

  return {
    ...advancedSearch,
    dependencies,
    onInput,
    simpleSearchKey: 'name',
    resource: filtersResource,
    dataResource
  };
};
